import { DispatcherEvent } from '@carvana/merch-native/index';
import { useContext } from 'react';
import { PrimaryButton } from '@/common/Button';
import TrackWrapper from '@/common/TrackWrapper';
import { fireAction } from '@/home/HeroCardsReturnUser/Cards/actionNames';
import LoadingCard, {
  CardType
} from '@/home/HeroCardsReturnUser/Cards/LoadingCard';
import useHasPurchaseInProgress from '@/home/hooks/useHasPurchaseInProgress';
import formatEvent from '@/home/utils/formatEvent';
import useGetTradeInStatus from '~/components/home/hooks/useGetTradeStatus';
import COOKIES from '~/constants/cookies';
import Sections from '~/constants/home/sections';
import { useIsMobile } from '~/context/common/DeviceContext';
import TradeContext, { useIsTradeLoaded } from '~/context/common/TradeContext';
import { useNextBestActionIsLoaded } from '~/context/home/NextBestActionContext';
import { TransactionIntent } from '~/contracts/common/Trade';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import { nativeSrpPayload, nativeStcPayload } from '~/native/home/utils';
import BrowserCookies from '~/utilities/BrowserCookies';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import {
  activeTradeStatuses,
  TradeStatus,
  getTradeCtaDetails
} from './constants';
import Details from './Details';
import Status from './Status';
import DefaultCardImage from '../DefaultCardImage';
import {
  CardContainer,
  Heading,
  HeadingContainer,
  TradeGridWrapper
} from '../styles';

function TradeCard() {
  const { statusAgnosticTrade } = useContext(TradeContext);
  const {
    transactionIntent = TransactionIntent.Unknown,
    daysToExpiration = 0,
    id = ''
  } = statusAgnosticTrade ?? {};
  const isNextActionLoaded = useNextBestActionIsLoaded();
  const hasPurchaseInProgress = useHasPurchaseInProgress();
  const isTradesLoaded = useIsTradeLoaded();
  const tradeStatus = useGetTradeInStatus();
  const hasTrade = tradeStatus !== TradeStatus.None;
  const isMobile = useIsMobile();
  const defaultHeading = isMobile ? (
    <>Get a real offer for your car</>
  ) : (
    <>
      Get a real offer
      <br /> for your car
    </>
  );
  const headingText =
    transactionIntent === TransactionIntent.Trade
      ? 'Your Trade-in'
      : 'Sell/Trade';
  const heading = hasTrade ? headingText : defaultHeading;
  const nativePayloadToUse = activeTradeStatuses[tradeStatus]
    ? nativeSrpPayload
    : nativeStcPayload;

  const { ctaText, ctaHref } = getTradeCtaDetails(
    tradeStatus,
    transactionIntent,
    id
  );

  const commonCustomAttributes = {
    description: 'STC card',
    expireDays: daysToExpiration,
    status: tradeStatus === TradeStatus.None ? 'default' : tradeStatus,
    cta: ctaText
  };

  const hideCTA =
    hasPurchaseInProgress &&
    [TradeStatus.Active, TradeStatus.ExpiringSoon].includes(tradeStatus);

  return isTradesLoaded && isNextActionLoaded ? (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', commonCustomAttributes)}
      trackOnce
      key={tradeStatus}
    >
      <CardContainer
        className="gap-16 m:gap-8"
        isDefault={!hasTrade}
        isTradeCard
      >
        <TradeGridWrapper defaultLayout={!hasTrade}>
          <HeadingContainer defaultLayout={!hasTrade}>
            <Heading data-qa="trade-card-heading">{heading}</Heading>
            {hasTrade && <Status tradeStatus={tradeStatus} />}
          </HeadingContainer>

          <DefaultCardImage
            defaultLayout={!hasTrade}
            url={'/images/graph-sell-or-trade.png'}
            altText="graph"
          />

          <Details ctaText={ctaText} />
        </TradeGridWrapper>

        <a
          data-analytics-id="Home feed card"
          data-analytics-attributes={JSON.stringify({
            ...commonCustomAttributes,
            item: 'cta button'
          })}
          href={ctaHref}
          data-qa={'trade-card-cta'}
          className={hideCTA ? 'hidden' : ''}
          data-native-payload={nativePayloadToUse}
          onClick={() => {
            fireAction(CardType.Trade);
            BrowserCookies.remove(COOKIES.tradeInApplied);
            sendMessageToNative(DispatcherEvent.ToggleTradeIn, {
              isTradeInApplied: true
            });
          }}
        >
          <PrimaryButton className="h-40 min-h-[40px] w-full">
            {ctaText}
          </PrimaryButton>
        </a>
      </CardContainer>
    </TrackWrapper>
  ) : (
    <LoadingCard card={CardType.Trade} />
  );
}

export default withErrorBoundary(
  TradeCard,
  undefined,
  undefined,
  Sections.TradeCard
);
